import * as service from "../service";
import { store } from "../../store";
import { cloudinaryUploadList } from "../../middleware/uploadImage";
import { toTwelveHourClock } from "@/utils/activityFormat";
import type {
  ActivityFormPayload,
  ActivityInfo,
  ActivityJoinRow,
  ActivityParticipant,
  GetActivitiesResponse,
  GetJoiningActivitiesResponse,
  GetCreatedActivitiesResponse,
  CreateActivityResponse,
  EditActivityResponse,
  DeleteActivityResponse,
  JoinActivityResponse,
  AcceptJoinActivityResponse,
  RejectJoinActivityResponse,
  CheckJoinActivityResponse,
  GetParticipantsResponse,
} from "./types";

/**
 * Serialize the activity form into the create body `createActivity` expects.
 * Times are stored as `hh:mm a`; `category` is an ActivityCategory enum value.
 */
export const toActivityFormBody = (
  payload: ActivityFormPayload,
  cdnPath = "activity",
) => ({
  activity_image: payload.imageUrls,
  cdn_path: cdnPath,
  title: payload.title,
  fee: payload.fee,
  max_participants: payload.maxParticipants,
  date: payload.date,
  time: toTwelveHourClock(payload.time),
  meeting_date: payload.meetingDate,
  meeting_time: toTwelveHourClock(payload.meetingTime),
  deadline_date: payload.deadlineDate,
  deadline_time: toTwelveHourClock(payload.deadlineTime),
  location: payload.location,
  meeting_location: payload.meetingLocation,
  description: payload.description,
  category: payload.category || null,
});

/**
 * Serialize the activity form into the edit body `editActivity` expects. The
 * edit route does not accept images, so only the editable fields are sent.
 */
export const toActivityEditBody = (payload: ActivityFormPayload) => ({
  title: payload.title,
  max_participants: payload.maxParticipants,
  fee: payload.fee,
  date: payload.date,
  time: toTwelveHourClock(payload.time),
  meeting_date: payload.meetingDate,
  meeting_time: toTwelveHourClock(payload.meetingTime),
  deadline_date: payload.deadlineDate,
  deadline_time: toTwelveHourClock(payload.deadlineTime),
  location: payload.location,
  meeting_location: payload.meetingLocation,
  description: payload.description,
  category: payload.category || null,
});

/** Fetch a 20-row page of the activity feed. Backend: GET .../getAllActivities. */
export const getAllActivities = async (
  page: number = 1,
): Promise<GetActivitiesResponse> => {
  try {
    const resp = await service.call(
      "GET",
      "api/activity/getActivity/getAllActivities",
      {
        page,
      },
    );
    return resp;
  } catch (err) {
    console.error(`getAllActivities ${err}`);
    return { status: -1 } as any;
  }
};

/** Fetch a 20-row page of activities the caller has joined. Backend: GET .../getJoiningActivities. */
export const getJoiningActivities = async (
  page: number = 1,
): Promise<GetJoiningActivitiesResponse> => {
  try {
    const resp = await service.call(
      "GET",
      "api/activity/getActivity/getJoiningActivities",
      {
        page,
      },
    );
    return resp;
  } catch (err) {
    console.error(`getJoiningActivities ${err}`);
    return { status: -1 } as any;
  }
};

/** Fetch a 20-row page of activities owned by the caller. Backend: GET .../getCreatedActivities. */
export const getCreatedActivities = async (
  page: number = 1,
): Promise<GetCreatedActivitiesResponse> => {
  try {
    const resp = await service.call(
      "GET",
      "api/activity/getActivity/getCreatedActivities",
      {
        page,
      },
    );
    return resp;
  } catch (err) {
    console.error(`getCreatedActivities ${err}`);
    return { status: -1 } as any;
  }
};

/**
 * Create an activity. Picked images are uploaded to Cloudinary first; the
 * backend re-hosts the URLs under the `cdn_path` folder. Ownership is derived
 * from the token, so no owner id is sent.
 * Backend: POST /api/activity/createActivity/createActivity
 */
export const createActivity = async (
  payload: ActivityFormPayload,
): Promise<CreateActivityResponse> => {
  const { user } = store.getState().auth as any;
  try {
    const uploadedImages = await cloudinaryUploadList(payload.imageUrls);
    const resp = await service.call(
      "POST",
      "api/activity/createActivity/createActivity",
      {
        user_id: user?.id,
        ...toActivityFormBody(
          { ...payload, imageUrls: uploadedImages },
          user?.id ? `activity/${user.id}` : "activity",
        ),
      },
    );
    return resp;
  } catch (err) {
    console.error(`createActivity ${err}`);
    return { status: -1 } as any;
  }
};

/** Update an existing activity (owner/admin only). Backend: POST .../editActivity. */
export const editActivity = async (
  activityId: string,
  payload: ActivityFormPayload,
): Promise<EditActivityResponse> => {
  try {
    const resp = await service.call(
      "POST",
      "api/activity/editActivity/editActivity",
      {
        id: activityId,
        ...toActivityEditBody(payload),
      },
    );
    return resp;
  } catch (err) {
    console.error(`editActivity ${err}`);
    return { status: -1 } as any;
  }
};

/** Delete an activity (owner/admin only). Backend: POST .../deleteActivity. */
export const deleteActivity = async (
  activityId: string,
): Promise<DeleteActivityResponse> => {
  try {
    const resp = await service.call(
      "POST",
      "api/activity/deleteActivity/deleteActivity",
      {
        id: activityId,
      },
    );
    return resp;
  } catch (err) {
    console.error(`deleteActivity ${err}`);
    return { status: -1 } as any;
  }
};

/** Join an activity. Backend: POST .../joinActivity. */
export const joinActivity = async (
  activityId: string,
  coordinatorId?: string | null,
): Promise<JoinActivityResponse> => {
  try {
    const resp = await service.call(
      "POST",
      "api/activity/joinActivity/joinActivity",
      {
        activity_id: activityId,
        coordinator_id: coordinatorId,
      },
    );
    return resp;
  } catch (err) {
    console.error(`joinActivity ${err}`);
    return { status: -1 } as any;
  }
};

/** Accept an applicant (owner only). Backend: PUT .../acceptJoinActivity. */
export const acceptJoinActivity = async (
  joinId: string,
): Promise<AcceptJoinActivityResponse> => {
  try {
    const resp = await service.call(
      "PUT",
      "api/activity/joinActivity/acceptJoinActivity",
      {
        id: joinId,
      },
    );
    return resp;
  } catch (err) {
    console.error(`acceptJoinActivity ${err}`);
    return { status: -1 } as any;
  }
};

/** Reject an applicant (owner only). Backend: PUT .../rejectJoinActivity. */
export const rejectJoinActivity = async (
  joinId: string,
): Promise<RejectJoinActivityResponse> => {
  try {
    const resp = await service.call(
      "PUT",
      "api/activity/joinActivity/rejectJoinActivity",
      {
        id: joinId,
      },
    );
    return resp;
  } catch (err) {
    console.error(`rejectJoinActivity ${err}`);
    return { status: -1 } as any;
  }
};

/**
 * Check whether the caller has joined an activity. Returns the join row when
 * joined (`status 0`), otherwise `status 1`.
 * Backend: POST /api/activity/checkActivity/checkJoinActivity
 */
export const checkJoinActivity = async (
  activityId: string,
): Promise<CheckJoinActivityResponse> => {
  try {
    const resp = await service.call(
      "POST",
      "api/activity/checkActivity/checkJoinActivity",
      {
        activity_id: activityId,
      },
    );
    return resp;
  } catch (err) {
    console.error(`checkJoinActivity ${err}`);
    return { status: -1 } as any;
  }
};

/** List every applicant of an activity (owner only). */
export const getAllActivitiesParticipants = async (
  activityId: string,
): Promise<GetParticipantsResponse> => {
  try {
    const resp = await service.call(
      "GET",
      "api/activity/getActivityParticipants/getAllActivitiesParticipants",
      {
        activity_id: activityId,
      },
    );
    return resp;
  } catch (err) {
    console.error(`getAllActivitiesParticipants ${err}`);
    return { status: -1 } as any;
  }
};

/** List the accepted participants of an activity (owner only). */
export const getActivitiesParticipants = async (
  activityId: string,
): Promise<GetParticipantsResponse> => {
  try {
    const resp = await service.call(
      "GET",
      "api/activity/getActivityParticipants/getActivitiesParticipants",
      {
        activity_id: activityId,
      },
    );
    return resp;
  } catch (err) {
    console.error(`getActivitiesParticipants ${err}`);
    return { status: -1 } as any;
  }
};

export type {
  ActivityInfo,
  ActivityJoinRow,
  ActivityParticipant,
};
